<template>
  <a-modal
    :wrapClassName="'modal-xl'"
    v-model:visible="visible"
    title="Altas y bajas al stock"
  >
    <a-typography-text>
      Historial de altas y bajas al stock realizadas:
    </a-typography-text>

    <a-table
      class="mt-3"
      :data-source="table_data"
      :columns="columns"
      :loading="loadingStockFiles"
      :pagination="{
        current: pagination_props.current,
        pageSize: pagination_props.pageSize,
        total: pagination_props.total
      }"
      @change="changeCurrentPage"
      :scroll="{ x: 'max-content' }"
    >
      <template #downloadRender="{ record }">
        <a-tooltip color="#05045D" :title="`Descargar archivo`">
          <a-button
            class="p-0"
            type="link"
            target="_blank"
            @click="downloadStockFile(record.id)"
          >
            {{ record.fileName }}
          </a-button>
        </a-tooltip>
      </template>
      <template #downloadReportRender="{ record }">
        <span style="display: flex">
          <a-tooltip color="#05045D" :title="`Descargar reporte`">
            <a-button
              class="p-0"
              type="link"
              target="_blank"
              @click="downloadProcessedFile(record.processedFile.id)"
            >
              {{ record.processedFile.name || 'Descargar reporte' }}
            </a-button>
          </a-tooltip>
        </span>
      </template>
      <template #observationsRender="{ record }">
        <span style="display: flex">
          {{ record.processedFile.numberOfObservations ?? 'Desconocido' }}
        </span>
      </template>
      <template #tag="{ text: tag }">
        <tag type="stock" :id="tag.id" :text="tag.tag" />
      </template>
    </a-table>
    <template #footer>
      <a-button type="primary" @click="closeModal">Cerrar</a-button>
    </template>
  </a-modal>
</template>

<script>
import { ref, computed, watch, reactive } from 'vue';
import { useRoute } from 'vue-router';
import { getProcessedFile, getStockFile } from '@/network';
import { useStore } from 'vuex';
import {
  FETCH_STOCK_FILES,
  GET_STOCK_FILES,
  GET_LOADING_STOCK_FILES
} from '@/store/types';
import tag from '@/components/massive-tags.vue';

export default {
  components: {
    tag
  },

  setup() {
    const visible = ref(false);
    const loading = ref(false);
    const store = useStore();
    const table_data = ref([]);
    const route = useRoute();
    const companyId = route.params.id;

    const loadingStockFiles = computed(
      () => store.getters[GET_LOADING_STOCK_FILES]
    );

    // Ejemplo de columnas para la tabla principal
    const columns = ref([
      {
        title: 'id',
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: 'Tipo',
        dataIndex: 'type',
        key: 'type',
        slots: {
          customRender: 'tag'
        }
      },
      {
        title: 'Aseguradora',
        dataIndex: 'insuranceCompany',
        key: 'insuranceCompany'
      },
      {
        title: 'Ejecutivo',
        dataIndex: 'broker',
        key: 'broker'
      },
      {
        title: 'Fecha de ingreso',
        dataIndex: 'createdAt',
        key: 'createdAt'
      },
      {
        title: 'Archivo original',
        dataIndex: 'fileDownload',
        key: 'fileDownload',
        slots: { customRender: 'downloadRender' }
      },
      {
        title: 'N° filas',
        dataIndex: 'numberOfRows',
        key: 'numberOfRows'
      },
      {
        title: 'Reporte',
        dataIndex: 'report',
        key: 'report',
        slots: { customRender: 'downloadReportRender' }
      },
      {
        title: 'N° observaciones',
        dataIndex: 'numberOfObservations',
        key: 'numberOfObservations',
        slots: { customRender: 'observationsRender' }
      }
    ]);

    const pagination_props = reactive({
      total: 0,
      pageSize: 10,
      current: 1
    });

    const allStockFiles = computed(() => store.getters[GET_STOCK_FILES]);

    const fetchStockFiles = async () => {
      try {
        await store.dispatch(FETCH_STOCK_FILES, {
          companyId,
          page: pagination_props.current,
          size: pagination_props.pageSize
        });
      } catch (e) {
        console.error('Error al obtener archivos de stock:', e);
      }
    };

    const downloadStockFile = (fileStockId) => {
      loading.value = true;
      try {
        const url = getStockFile(fileStockId);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'archivo_original.xlsx';
        link.click();
      } catch (e) {
        console.error(
          'Error al formar la URL o descargar archivo original:',
          e
        );
      } finally {
        loading.value = false;
      }
    };

    const downloadProcessedFile = (id) => {
      loading.value = true;
      try {
        const url = getProcessedFile(id);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'reporte.xlsx';
        link.click();
      } catch (e) {
        console.error('Error al formar la URL o descargar reporte:', e);
      } finally {
        loading.value = false;
      }
    };

    watch(allStockFiles, (newVal) => {
      updatePaginationFromResponse(newVal);
    });

    const openModal = async () => {
      visible.value = true;
      pagination_props.current = 1;
      await fetchStockFiles();
    };

    const changeCurrentPage = async (pagination) => {
      pagination_props.current = pagination.current;
      await fetchStockFiles();
    };

    const closeModal = async () => {
      visible.value = false;
    };

    const updatePaginationFromResponse = (response) => {
      table_data.value = response.rows;
      pagination_props.total = response.total_items;
      pagination_props.current = response.page;
      pagination_props.pageSize = response.items_per_page;
    };

    return {
      visible,
      table_data,
      pagination_props,
      columns,
      loading,
      changeCurrentPage,
      openModal,
      closeModal,
      fetchStockFiles,
      allStockFiles,
      downloadStockFile,
      downloadProcessedFile,
      loadingStockFiles
    };
  }
};
</script>
